import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Modal, Button } from 'reactstrap';
import axios from 'axios';
import history from '../../../config/history';
import './ToolingRecords.scss';

// Training video thumbnail image
import thumbnail1 from '../../../images/ToolingRecords.png';

class ToolingRecords extends React.Component { // eslint-disable-line react/prefer-stateless-function
    static propTypes = {};

    static defaultProps = {};


    constructor(props) {
        super(props);
        this.state = {
            videoModalOpen: false,
            videoUrl: null,
            isLoadingVideo: false,
            error: null,
            videoThumbnails: {},
            videoRef: React.createRef(),
            // Item Search authentication modal
            itemSearchModalOpen: false,
            itemSearchError: null,
            // Video metadata
            videos: [
                {
                    key: 'iconvalor.mp4', // Actual filename in the S3 bucket Tooling-Records folder
                    title: 'Tooling Location Basics',
                    description: 'Learn how to search and locate foundry and forging tooling in the system.',
                    duration: '4:13',
                    thumbnail: thumbnail1,
                }
                // Add more videos here when available
            ]
        };
    }

    // Helper to get idToken from cookies
    static getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    // Function to get video URL from API
    getVideoUrl = async (videoKey) => {
        this.setState({ isLoadingVideo: true, error: null });
        try {
            // Get the base API URL from environment variables
            const apiUrl = process.env.REACT_APP_API_HOST;

            const token = ToolingRecords.getCookie('idToken');

            // Check if user is logged in first
            if (!token) {
                // Store current page URL for redirect after login
                sessionStorage.setItem('auth_redirect_url', window.location.pathname);

                this.setState({
                    error: 'Please log in to view this video. You must be a member to access training content.',
                    isLoadingVideo: false
                });
                return null;
            }

            const response = await axios.post(
                `${apiUrl}/api/videos/playback-url`,
                { videoKey },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );

            // Update state with the video URL
            this.setState({
                videoUrl: response.data.url,
                isLoadingVideo: false
            });

            return response.data.url;
        } catch (error) {
            console.error('Error fetching video URL:', error);
            this.setState({
                error: 'Failed to load video. Please try again later.',
                isLoadingVideo: false
            });
            return null;
        }
    }

    // Generate video thumbnail from the video itself
    generateVideoThumbnail = (videoUrl, videoKey) => new Promise((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = videoUrl;
        video.muted = true;
        video.preload = 'metadata';

        // When metadata is loaded, we can get the duration
        video.onloadedmetadata = () => {
            // Seek to 25% of the video duration for a representative frame
            const seekTime = video.duration * 0.25;
            video.currentTime = seekTime;

            // When the frame is loaded, capture it
            video.onseeked = () => {
                // Create a canvas to capture the frame
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                // Draw the video frame to the canvas
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Add duration overlay
                ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
                ctx.fillRect(canvas.width - 80, canvas.height - 30, 70, 25);
                ctx.fillStyle = 'white';
                ctx.font = '16px Arial';

                // Format duration as mm:ss
                const minutes = Math.floor(video.duration / 60);
                const seconds = Math.floor(video.duration % 60);
                const formattedDuration = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

                ctx.fillText(formattedDuration, canvas.width - 75, canvas.height - 12);

                // Convert canvas to data URL
                const thumbnailUrl = canvas.toDataURL('image/jpeg');

                // Update state with the new thumbnail
                this.setState(prevState => ({
                    videoThumbnails: {
                        ...prevState.videoThumbnails,
                        [videoKey]: {
                            url: thumbnailUrl,
                            duration: formattedDuration
                        }
                    }
                }));

                resolve({
                    url: thumbnailUrl,
                    duration: formattedDuration
                });
            };
        };

        // Handle errors
        video.onerror = () => {
            console.error('Error generating thumbnail');
            resolve(null);
        };
    });

    // Open video modal and get video URL
    openVideoModal = async (video) => {
        this.setState({
            videoModalOpen: true,
            videoUrl: null,
            isLoadingVideo: true
        });

        const url = await this.getVideoUrl(video.key);

        // If we don't have a thumbnail for this video yet, generate one
        if (url && !this.state.videoThumbnails[video.key]) {
            this.generateVideoThumbnail(url, video.key);
        }
    }

    // Close video modal.
    closeVideoModal = () => {
        this.setState({
            videoModalOpen: false,
            videoUrl: null
        });
    }

    // Handle Item Search button click
    handleItemSearchClick = () => {
        const token = ToolingRecords.getCookie('idToken');

        // Check if user is logged in first
        if (!token) {
            // Store the Item Search URL for redirect after login
            sessionStorage.setItem('auth_redirect_url', '/tools/locator');

            this.setState({
                itemSearchModalOpen: true,
                itemSearchError: 'Please log in to access Item Search. You must be a member to access this feature.'
            });
        } else {
            // User is logged in, redirect directly
            history.push('/tools/locator');
        }
    }

    // Close Item Search modal
    closeItemSearchModal = () => {
        this.setState({
            itemSearchModalOpen: false,
            itemSearchError: null
        });
    }


    componentDidMount() {
        window.scrollTo({ top: 0 });
    }

    render() {
        const state = this.state;
        const {
            videoModalOpen,
            videoUrl,
            isLoadingVideo,
            error,
            videos,
            itemSearchModalOpen,
            itemSearchError
        } = state;

        return (
            <div className="tooling-records-page">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>NFFS ICON - Tooling Records</title>
                </Helmet>

                <Container className="content-container mt-5 mb-5">
                    <Row>
                        <Col>
                            <h1 className="page-title mb-4">Tooling Records</h1>
                            <div className="content-box p-4">
                                <h2>Access to foundry, forging, and machining tooling locations and availability</h2>
                                <p className="lead">
                                    Our Tooling Records system provides comprehensive information about foundry, forging, and machining tooling
                                    across various locations and their current availability status.
                                </p>
                                <hr />
                                <h3>Key features:</h3>
                                <ul>
                                    <li>Centralized database of tooling records across multiple facilities</li>
                                    <li>Real-time availability tracking and updates</li>
                                    <li>Detailed specifications including dimensions, materials, and capabilities</li>
                                    <li>Location mapping and logistics information</li>
                                    <li>Historical usage data and performance metrics</li>
                                </ul>

                                <div className="mt-4">
                                    <button
                                        onClick={this.handleItemSearchClick}
                                        className="btn btn-lg"
                                        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', color: 'white' }}
                                    >
                                        <i className="fas fa-search me-3" /> &nbsp;
                                        Search Tooling, Inventory and Procurement History
                                    </button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Training Videos Section */}
                    <Row className="mt-5">
                        <Col xs={12}>
                            <h2 className="mb-4">Videos</h2>
                        </Col>
                    </Row>
                    <Row className="mb-5">
                        {videos.map((video, index) => {
                            const thumbnail = this.state.videoThumbnails[video.key];
                            return (
                                <Col xs={12} md={4} className="mb-4" key={index}>
                                    <div className="video-thumbnail-card">
                                        <div className="video-thumbnail" onClick={() => this.openVideoModal(video)}>
                                            {/* Use generated thumbnail if available, otherwise use placeholder */}
                                            <img
                                                src={thumbnail ? thumbnail.url : video.thumbnail}
                                                alt={video.title}
                                                className="img-fluid"
                                            />
                                            <div className="play-button">
                                                <i className="fa fa-play-circle-o" />
                                            </div>
                                            {/* Show duration overlay on thumbnail */}
                                            <div className="duration-badge">
                                                {thumbnail ? thumbnail.duration : video.duration}
                                            </div>
                                        </div>
                                        <div className="video-info p-3">
                                            <h5>{video.title}</h5>
                                            <p className="text-muted">{video.description}</p>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </Container>

                {/* Video Modal - Simplified with banner */}
                <Modal isOpen={videoModalOpen} toggle={this.closeVideoModal} className="video-modal" size="lg">
                    {isLoadingVideo && (
                        <div className="text-center p-5">
                            <div className="fa fa-spinner fa-spin fa-3x text-primary" />
                            <p className="mt-3">Loading video...</p>
                        </div>
                    )}

                    {error && (
                        <div className="text-center p-5">
                            <div className="alert alert-danger">{error}</div>
                            {error.includes('Please log in') && (
                                <div className="mt-3">
                                    <Link to="/login" className="btn mr-2" style={{ backgroundColor: '#ff914d', borderColor: '#ff914d', color: '#fff' }}>Sign In</Link>
                                    <Link to="/register" className="btn" style={{ backgroundColor: '#fff', borderColor: '#ef4136', color: '#ef4136' }}>Register</Link>
                                </div>
                            )}
                            <div className="mt-3">
                                <Button style={{ backgroundColor: '#000000', borderColor: '#000000', color: '#fff' }} onClick={this.closeVideoModal}>Close Video</Button>
                            </div>
                        </div>
                    )}

                    {videoUrl && (
                        <React.Fragment>
                            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                            <video
                                ref={this.state.videoRef}
                                controls
                                autoPlay
                                className="video-player fullscreen"
                                controlsList="nodownload"
                                playsInline
                                onLoadedMetadata={(e) => {
                                    // Update video duration when metadata is loaded
                                    const minutes = Math.floor(e.target.duration / 60);
                                    const seconds = Math.floor(e.target.duration % 60);
                                    const formattedDuration = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
                                    console.log(`Video duration: ${formattedDuration}`);
                                }}
                            >
                                <source src={videoUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </React.Fragment>
                    )}
                </Modal>

                {/* Item Search Authentication Modal */}
                <Modal isOpen={itemSearchModalOpen} toggle={this.closeItemSearchModal} className="item-search-modal">
                    <div className="text-center p-5">
                        <div className="alert alert-danger">{itemSearchError}</div>
                        <div className="mt-3">
                            <Link to="/login" className="btn mr-2" style={{ backgroundColor: '#ff914d', borderColor: '#ff914d', color: '#fff' }}>Sign In</Link>
                            <Link to="/register" className="btn" style={{ backgroundColor: '#fff', borderColor: '#ef4136', color: '#ef4136' }}>Register</Link>
                        </div>
                        <div className="mt-3">
                            <Button style={{ backgroundColor: '#000000', borderColor: '#000000', color: '#fff' }} onClick={this.closeItemSearchModal}>Close</Button>
                        </div>
                    </div>
                </Modal>

            </div>
        );
    }
}

export default ToolingRecords;



// WEBPACK FOOTER //
// ./src/pages/static/ToolingRecords/ToolingRecords.jsx